import m from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {Button, Icon} from '@bitstillery/common/components/portal'
import {$t, api, store} from '@bitstillery/common/app'
import {MithrilComponent} from '@bitstillery/mithril'
import {OrderItems} from '@/components/order_items/order_items'
import {CartErrors} from '@/components/panels/panel_cart/components/errors/errors'
import {CartConfirmations} from '@/components/panels/panel_cart/components/confirmations/confirmations'
import {TotalCosts} from '@/components/total_costs/total_costs'
import {CheckoutForm} from '@/components/pages/checkout/components/form/form'

import {$m, $s} from '@/app'

import type {GetActiveVouchersResponse} from '@bitstillery/common/api/fact2server'

interface PanelCartAttrs {
    mode: 'shop' | 'checkout'
}

export class PanelCart extends MithrilComponent<PanelCartAttrs> {
    async toggle_voucher(voucher: GetActiveVouchersResponse) {
        // Reloading the order clears its artkey until the reload lands.
        const order_artkey = $s.cart.artkey
        if (!order_artkey) {
            return
        }
        const voucher_active = $m.cart.voucher_additional()?.description === voucher.code
        if (voucher_active) {
            await api.delete(`portal/orders/${order_artkey}/vouchers/${voucher.artkey}`)
        } else {
            await api.post(
                `portal/orders/${order_artkey}/vouchers`,
                {
                    voucher_code: voucher.code,
                },
                true,
            )
        }
        await $m.order.load_current_order()
    }

    view(vnode: m.Vnode<PanelCartAttrs>) {
        const has_errors = $m.cart.has_errors()
        return (
            <div
                className={classes('shop-panel shop-panel-cart', 'shop-overlay shop-overlay-bottom', 'shop-overlay-right', {
                    collapsed: $s.panels.context.collapsed,
                    'shop-overlay-open': !$s.panels.context.collapsed,
                })}
            >
                <div
                    className={classes('c-panel-cart', {
                        collapsed: $s.panels.context.collapsed,
                    })}
                >
                    <div className='panel-title'>
                        <Icon name='cart' size='d' type='unset' />
                        <span>{$t('cart.panel_title')}</span>
                        {$s.cart.reference && <span className='order-reference'>{$s.cart.reference}</span>}
                    </div>
                    <div className='content'>
                        {!$s.panels.context.collapsed ? (
                            <div className='cart-mobile-sheet-controls cart-panel-narrow-only-strip'>
                                <button
                                    aria-label={$t('cart.sheet_collapse_tip')}
                                    className='cart-mobile-sheet-collapse-bar'
                                    type='button'
                                    onclick={() => {
                                        $s.panels.context.collapsed = true
                                        void store.save()
                                    }}
                                >
                                    <Icon
                                        className='cart-mobile-sheet-chevron--mobile'
                                        name='chevronDown'
                                        size='s'
                                        type='unset'
                                    />
                                    <Icon
                                        className='cart-mobile-sheet-chevron--tablet'
                                        name='chevronRight'
                                        size='s'
                                        type='unset'
                                    />
                                </button>
                            </div>
                        ) : null}
                        {vnode.attrs.mode === 'shop' && <OrderItems />}
                        {has_errors && (
                            <>
                                <CartErrors />
                                <CartConfirmations />
                            </>
                        )}
                        <TotalCosts />

                        {vnode.attrs.mode === 'shop' && (
                            <Button
                                className='btn-confirm-step mb-2 mt-3'
                                disabled={has_errors}
                                // @i18n include: breadcrumbs.checkout
                                link='/shop/checkout'
                                icon='cartRight'
                                onclick={async () => {
                                    await $m.cart.validate_cart()
                                }}
                                text={$t('checkout.status.step1_button')}
                                type='unset'
                            />
                        )}
                        {vnode.attrs.mode === 'checkout' && (
                            <>
                                {$s.cart.vouchers.available.length > 0 && (
                                    <div className='vouchers element'>
                                        <div className='label'>{$t('checkout.vouchers.label')}</div>
                                        <div className='item'>
                                            {(() => {
                                                const voucher_code = $m.cart.voucher_additional()?.description
                                                return $s.cart.vouchers.available.map((voucher) => (
                                                    <Button
                                                        className={voucher.code === voucher_code ? 'active' : ''}
                                                        disabled={
                                                            $m.cart.cart_units() === 0 &&
                                                            $s.cart.sales_order_items.length === 0 &&
                                                            !$m.cart.voucher_additional()
                                                        }
                                                        icon={voucher.code === voucher_code ? 'voucherOn' : 'voucher'}
                                                        onclick={() => this.toggle_voucher(voucher)}
                                                        text={voucher.code}
                                                        type='info'
                                                    />
                                                ))
                                            })()}
                                        </div>
                                    </div>
                                )}
                                <CheckoutForm />
                            </>
                        )}
                        {vnode.children}
                    </div>

                    {(() => {
                        const contact = $s.support.contact
                        if (!contact?.name) return null
                        const contact_phone = process.env.MSI_CONTACT_PHONE || contact.phone || ''
                        const contact_email = process.env.MSI_CONTACT_EMAIL || contact.email || ''
                        const {mobile} = contact
                        return (
                            <div className='cart-support'>
                                <div
                                    aria-hidden={!contact.img}
                                    className='cart-support-photo'
                                    style={contact.img ? {backgroundImage: `url(${contact.img})`} : undefined}
                                />
                                <div className='cart-support-info'>
                                    <div className='cart-support-name'>{contact.name}</div>
                                    {contact.role && <div className='cart-support-role'>{$t(contact.role)}</div>}
                                </div>
                                <div className='cart-support-actions'>
                                    <Button
                                        icon='email'
                                        onclick={() => {
                                            window.location.href = `mailto:${contact.email ?? contact_email}`
                                        }}
                                        tip={$t._('support.contact.email', {contact: contact_email})}
                                        type='info'
                                        variant='toggle'
                                    />

                                    <Button
                                        icon='phone'
                                        onclick={() => {
                                            window.location.href = `tel:${contact.phone ?? contact_phone}`
                                        }}
                                        tip={$t._('support.contact.phone', {contact: contact_phone})}
                                        type='info'
                                        variant='toggle'
                                    />
                                    {mobile ? (
                                        <Button
                                            icon='cellPhone'
                                            onclick={() => {
                                                window.location.href = `tel:${mobile}`
                                            }}
                                            tip={$t._('support.contact.mobile', {contact: mobile})}
                                            type='info'
                                            variant='toggle'
                                        />
                                    ) : null}
                                </div>
                            </div>
                        )
                    })()}
                </div>
            </div>
        )
    }
}
